/**
 * @file 復習カード (epic_23 §11) のクライアント。
 * @what: `GET/PUT/DELETE /families/{familyId}/study/review_cards[/{cardId}]` と、結果の記録
 *   `POST .../review_cards/{cardId}/reviews`。次の間隔と期日はサーバが決める。
 * @why: 間隔の決め方をサーバ (domain) に 1 か所で持ち、web と CLI (学習セッション) で揃える。
 *   一覧は家族の全カードで、学習者の絞り込みは client (model/reviewCards.ts)。
 * @failure: ApiError を伝播。
 */

import { apiFetch } from "@/shared/api/client";

export type StudyReviewResultKind = "ok" | "hard" | "ng";
export type StudyReviewCardStatus = "active" | "retired";

export interface StudyReviewCardRef {
  source_id: string;
  /** 紙のページ番号。0 = 資料全体 (省略される)。 */
  page?: number;
  /** 問 (scan_items) の id。 */
  item_id?: string;
}

export interface StudyReviewResult {
  date: string;
  result: StudyReviewResultKind;
}

export interface StudyReviewCard {
  id: string;
  learner_person_id: string;
  prompt: string;
  answer?: string;
  topic?: string;
  ref?: StudyReviewCardRef;
  /** 次に復習する日 (YYYY-MM-DD)。 */
  due_on: string;
  interval_days: number;
  reviews?: StudyReviewResult[];
  status: StudyReviewCardStatus;
  created_at: string;
  updated_at: string;
}

export interface StudyReviewCardPutRequest {
  learner_person_id: string;
  prompt: string;
  answer?: string;
  topic?: string;
  ref?: StudyReviewCardRef;
  due_on?: string;
  status?: StudyReviewCardStatus;
}

function cardsPath(familyId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/review_cards`;
}

export async function fetchReviewCards(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudyReviewCard[]> {
  const res = await apiFetch<{ cards: StudyReviewCard[] }>(
    cardsPath(familyId),
    signal ? { signal } : {},
  );
  return res.cards;
}

export async function putReviewCard(
  familyId: string,
  cardId: string,
  body: StudyReviewCardPutRequest,
): Promise<StudyReviewCard> {
  return apiFetch<StudyReviewCard>(`${cardsPath(familyId)}/${encodeURIComponent(cardId)}`, {
    method: "PUT",
    body,
  });
}

export async function deleteReviewCard(familyId: string, cardId: string): Promise<void> {
  await apiFetch<undefined>(`${cardsPath(familyId)}/${encodeURIComponent(cardId)}`, {
    method: "DELETE",
  });
}

export async function postReviewResult(
  familyId: string,
  cardId: string,
  body: { result: StudyReviewResultKind; date?: string },
): Promise<StudyReviewCard> {
  return apiFetch<StudyReviewCard>(`${cardsPath(familyId)}/${encodeURIComponent(cardId)}/reviews`, {
    method: "POST",
    body,
  });
}

export function newReviewCardId(): string {
  return `rc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
